import React from 'react';
import { createNewFogbender } from 'fogbender';
export { createNewFogbender } from 'fogbender';

const context = React.createContext(undefined);
context.displayName = "FogbenderProvider";
const useFogbender = () => {
  const fogbender = React.useContext(context);

  if (!fogbender) {
    throw new Error("No fogbender set, use FogbenderProvider to set one");
  }

  return fogbender;
};
const FogbenderProvider = ({
  fogbender,
  children
}) => {
  const defaultFogbender = React.useRef();
  const value = fogbender || defaultFogbender.current || (defaultFogbender.current = createNewFogbender());
  return React.createElement(context.Provider, {
    value: value
  }, children);
};

const FogbenderIsConfigured = ({
  children
}) => {
  const isConfigured = useIsConfigured();

  if (isConfigured) {
    return React.createElement(React.Fragment, null, children);
  } else {
    return null;
  }
};
function useIsConfigured() {
  const fogbender = useFogbender();
  return useFromSnapshot(async () => {
    return fogbender.isClientConfigured();
  }, false);
}
function useFromSnapshot(snapshotGen, initialValue) {
  const [value, setValue] = React.useState(initialValue);
  const fogbender = useFogbender();
  React.useEffect(() => {
    const unsub = [];

    const run = async () => {
      const snapshot = await snapshotGen();
      unsub.push(snapshot.subscribe(setValue));
    };

    run();
    return () => {
      unsub.forEach(u => u());
    };
  }, [fogbender]);
  return value;
}

const noopCleanup = () => {
  return new Promise(resolve => resolve(() => {}));
};
function useRenderComponent(componentRenderer) {
  React.useEffect(() => {
    const promise = componentRenderer();
    return () => {
      promise.then(cleanup => cleanup());
    };
  }, [componentRenderer]);
}

const FogbenderSimpleWidget = ({
  clientUrl,
  env,
  token
}) => {
  const [fogbender, setFogbender] = React.useState(undefined);
  React.useEffect(() => {
    const fb = createNewFogbender();
    fb.setClientUrl(clientUrl);
    fb.setEnv(env);
    fb.setToken(addVersion(token));
    setFogbender(fb);
  }, []);

  if (!fogbender) {
    return null;
  }

  return React.createElement(FogbenderProvider, {
    fogbender: fogbender
  }, React.createElement(FogbenderWidget, null));
};
const FogbenderWidget = () => {
  const divRef = React.useRef(null);
  useRenderIframe(divRef, false);
  return React.createElement("div", {
    ref: divRef
  });
};
const FogbenderHeadlessWidget = () => {
  const divRef = React.useRef(null);
  useRenderIframe(divRef, true);
  return React.createElement("div", {
    ref: divRef
  });
};

const useRenderIframe = (divRef, headless) => {
  const fogbender = useFogbender();
  useRenderComponent(React.useCallback(() => {
    if (divRef.current) {
      return fogbender.renderIframe({
        headless,
        rootEl: divRef.current
      });
    } else {
      return noopCleanup();
    }
  }, [fogbender, headless]));
};

const FogbenderFloatingWidget = ({
  verbose,
  openInNewTab,
  closeable,
  defaultOpen
}) => {
  useCreateFloatingWidget({
    verbose,
    openInNewTab,
    closeable,
    defaultOpen
  });
  return null;
};

const useCreateFloatingWidget = ({
  verbose,
  openInNewTab,
  closeable,
  defaultOpen
}) => {
  const fogbender = useFogbender();
  useRenderComponent(React.useCallback(() => {
    return fogbender.createFloatingWidget({
      verbose,
      openInNewTab,
      closeable,
      defaultOpen
    });
  }, [fogbender, verbose, openInNewTab, closeable]));
};

const FogbenderUnreadBadge = React.memo(() => {
  const divRef = React.useRef(null);
  useRenderUnreadBadge(divRef);
  return React.createElement("div", {
    ref: divRef
  });
});

const useRenderUnreadBadge = divRef => {
  const fogbender = useFogbender();
  useRenderComponent(React.useCallback(() => {
    if (divRef.current) {
      return fogbender.renderUnreadBadge({
        el: divRef.current
      });
    } else {
      return noopCleanup();
    }
  }, [fogbender]));
};

const FogbenderConfig = ({
  clientUrl,
  env,
  token
}) => {
  const fogbender = useFogbender();
  React.useEffect(() => {
    fogbender.setClientUrl(clientUrl);
    return () => {
      fogbender.setClientUrl(undefined);
    };
  }, [clientUrl]);
  React.useEffect(() => {
    fogbender.setEnv(env);
    return () => {
      fogbender.setEnv(undefined);
    };
  }, [clientUrl]);
  React.useEffect(() => {
    fogbender.setToken(addVersion(token));
    return () => {
      fogbender.setToken(undefined);
    };
  }, [token]);
  return null;
};

function addVersion(token) {
  if (token) {
    token.versions = token.versions || {};
    token.versions["fogbender-react"] = "0.2.11";
  }

  return token;
}

const FogbenderSimpleFloatie = ({
  token,
  clientUrl,
  openInNewTab,
  verbose,
  closeable
}) => {
  const fogbender = React.useMemo(createNewFogbender, []);
  return React.createElement(FogbenderProvider, {
    fogbender: fogbender
  }, React.createElement(FogbenderConfig, {
    clientUrl: clientUrl,
    token: token
  }), React.createElement(FogbenderIsConfigured, null, React.createElement(FogbenderFloatingWidget, {
    key: "" + verbose + ":" + openInNewTab + ":" + closeable,
    verbose: verbose,
    openInNewTab: openInNewTab,
    closeable: closeable
  })));
};

export { FogbenderConfig, FogbenderFloatingWidget, FogbenderHeadlessWidget, FogbenderIsConfigured, FogbenderProvider, FogbenderSimpleFloatie, FogbenderSimpleWidget, FogbenderUnreadBadge, FogbenderWidget, useFogbender };
//# sourceMappingURL=index.modern.js.map
